import React, { useRef, useState, useEffect } from "react";
import Chatboticon from "./Chatboticon";
import ChatForm from "./ChatForm";
import ChatMessage from "./ChatMessage";
import { kuInfo } from "./kuInfo";
import "./Chatbot.css";

const ChatBot = () => {
    const [chatHistory, setChatHistory] = useState([{
        hideInChat: true,
        role: "model",
        text: kuInfo
    }]);
    const [showChatbot, setShowChatbot] = useState(false);
    
    // State to control just the welcome popup
    const [showWelcomePopup, setShowWelcomePopup] = useState(true);
    
    const chatBodyRef = useRef();

    // NEW: Auto-hide welcome popup after 10 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowWelcomePopup(false);
        }, 20000);
        
        // Cleanup the timer if the component unmounts
        return () => clearTimeout(timer);
    }, []);

    // Define the default menu options
    const menuOptions = [
        { text: "What is KU", type: "chat" },
        { text: "How can I Join", type: "chat" },
    ];

    const generateBotResponse = async (history) => {
    const updateHistory = (text, action = null, isError = false) => {
        setChatHistory(prev => [
            ...prev.filter(msg => msg.text !== "Thinking..."),
            { role: "model", text, action, isError }
        ]);
    };

    const lastUserEntry = history[history.length - 1]?.text || "";
    const cleanQuery = lastUserEntry
        .replace("Using the details provided above, please address this query: ", "")
        .toLowerCase();

    const historyForAPI = history.map(({ role, text }) => ({ role, parts: [{ text }] }));

    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: historyForAPI }),
    };

    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error("API URL is not configured.");

        const response = await fetch(apiUrl, requestOptions);
        const data = await response.json();

        if (!response.ok) throw new Error(data.error?.message || "Something went wrong!");

        const apiResponseText = data.candidates[0].content.parts[0].text
            .replace(/\*\*(.*?)\*\*/g, "$1")
            .trim();
        updateHistory(apiResponseText);

    } catch (error) {
        console.warn("API Error encountered. Serving conversational offline fallback:", error);

        //  1. GREETINGS 
        if (
            cleanQuery === "hi" || cleanQuery === "hello" || cleanQuery === "hey" ||
            cleanQuery === "hii" || cleanQuery === "helo" || cleanQuery.includes("good morning") ||
            cleanQuery.includes("good afternoon") || cleanQuery.includes("good evening") ||
            cleanQuery.includes("howdy") || cleanQuery.includes("what's up") ||
            cleanQuery.includes("whats up") || cleanQuery.includes("namaste")
        ) {
            updateHistory(
                "Hello there! Warm greetings from the Kindness Unlimited family! 🌸\n\n" +
                "I'm your KU Assistant, and I'm so glad you stopped by. Whether you're curious about who we are, " +
                "want to volunteer, donate, or just learn more about kindness — I'm here to help!\n\n" +
                "What would you like to know today? 😊"
            );

        //  2. ABOUT KU ─
        } else if (
            cleanQuery.includes("what is ku") || cleanQuery.includes("about ku") ||
            cleanQuery.includes("about kindness unlimited") || cleanQuery.includes("tell me about") ||
            cleanQuery.includes("who are you") || cleanQuery.includes("what do you do") || cleanQuery.includes("what does")
        ) {
            updateHistory(
                "That's a fantastic question! I'd love to share more about who we are. 🌟\n\n" +
                "**Kindness Unlimited (KU)** is a youth-centric, volunteer-led organization established in Mumbai back in 2005. " +
                "Our core mission is to promote social and economic change through the simple, yet profound, power of kindness.\n\n" +
                "We are also incredibly proud to be the peak national body in India representing the **World Kindness Movement**!\n\n" +
                "Does that help give you a better picture of our mission? Let me know if you'd like to hear about our core projects!"
            );

        //  3. JOIN / VOLUNTEER / SIGNUP 
        } else if (
            cleanQuery.includes("login") || cleanQuery.includes("signup") || cleanQuery.includes("sign up") ||
            cleanQuery.includes("how can i join") || cleanQuery.includes("volunteer") || cleanQuery.includes("how to join") ||
            cleanQuery.includes("register") || cleanQuery.includes("become a member")
        ) {
            updateHistory(
                "It's wonderful to hear you want to get involved! We are always thrilled to welcome new changemakers to our community. ✨\n\n" +
                "Joining us is super simple and incredibly rewarding:\n" +
                "1. Go to our website and log in.\n" +
                "2. Fill in your personal details to complete your profile.\n" +
                "3. Browse and join the various volunteering activities we organize.\n" +
                "4. Complete activities to earn special rewards and make a real impact!\n\n" +
                "Ready to take the first step and start your journey with us?",
                { text: "Register Now", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  4. DONATE 
        } else if (
            cleanQuery.includes("donate") || cleanQuery.includes("donation") ||
            cleanQuery.includes("money") || cleanQuery.includes("fund") ||
            cleanQuery.includes("contribute") || cleanQuery.includes("financial") ||
            cleanQuery.includes("sponsorship") || cleanQuery.includes("support us")
        ) {
            updateHistory(
                "Thank you so much for considering a contribution! Your generosity directly fuels our on-ground impact. ❤️\n\n" +
                "KU is a **Registered Trust**, **GuideStar Certified**, and **80G Tax Exemption Applicable** — so your donation is transparent and tax-deductible.\n\n" +
                "Every contribution, big or small, helps us spread more kindness.",
                { text: "Donate Securely", url: "https://kindnessunlimited.in/donate" }
            );

        //  5. PROJECTS 
        } else if (
            cleanQuery.includes("project") || cleanQuery.includes("nashta") ||
            cleanQuery.includes("plant kindness") || cleanQuery.includes("initiative") ||
            cleanQuery.includes("programme") || cleanQuery.includes("program")
        ) {
            updateHistory(
                "We have several exciting and impactful initiatives going on! Here is a quick look at how we are driving change on the ground: 🚀\n\n" +
                "- 🍌 **Project Nashta:** We provide culturally contextual nutritional meals to underprivileged children.\n" +
                "- 🌳 **Plant Kindness:** An environmental initiative where we plant fruit trees with the Kathkari tribe for sustainable livelihoods.\n" +
                "- 🎓 **Project KUL:** A youth-focused volunteering structure operating across educational institutions.\n" +
                "- 🏖️ **Beach Clean-ups:** Regular coastal clean-up drives across Mumbai.\n" +
                "- 📚 **Teaching & Tutoring:** Knowledge-sharing sessions for underprivileged children.\n\n"
            );

        //  6. MISSION / VISION / VALUES 
        } else if (
            cleanQuery.includes("mission") || cleanQuery.includes("vision") ||
            cleanQuery.includes("philosophy") || cleanQuery.includes("values") ||
            cleanQuery.includes("belief") || cleanQuery.includes("goal") ||
            cleanQuery.includes("purpose")
        ) {
            updateHistory(
                "I'd love to share our guiding principles with you! 🌍\n\n" +
                "**Our Mission:** To transform mindsets to encourage more sharing of time, resources, skills, knowledge, and money, and to celebrate the up-standers in society.\n\n" +
                "**Our Vision:** A society where socio-economic progress is deeply rooted in collective empathy and compassion for humanity.\n\n" +
                "**Our Philosophy:** Kindness is about balancing self-interest with the Common Good.\n\n"
            );

        //  7. KINDNESS JAM / EVENTS 
        } else if (
            cleanQuery.includes("jam") || cleanQuery.includes("event") ||
            cleanQuery.includes("festival") || cleanQuery.includes("concert") ||
            cleanQuery.includes("kindness jam") || cleanQuery.includes("upcoming")
        ) {
            updateHistory(
                "Oh, you are going to love **The Kindness Jam**! 🎉\n\n" +
                "It is our flagship annual event organized to celebrate **World Kindness Day (November 13)** through music, laughter, and generosity.\n\n" +
                "The best part? It features a unique **'pay-it-forward'** concept — entry isn't about cash, it's about kindness! " +
                "Volunteers earn their tickets by dedicating their time to NGO activities like beach clean-ups, tree planting, and teaching.\n\n" +
                "Stay tuned to our website and social media for the next event announcement!",
                { text: "View Events", url: "https://kindnessunlimited.in/events" }
            );

        //  8. IMPACT / NUMBERS 
        } else if (
            cleanQuery.includes("impact") || cleanQuery.includes("achieve") ||
            cleanQuery.includes("statistic") || cleanQuery.includes("number") ||
            cleanQuery.includes("how many") || cleanQuery.includes("reach")
        ) {
            updateHistory(
                "We are incredibly proud of what our community has accomplished together! 📈\n\n" +
                "Over two decades, Kindness Unlimited has built a massive network of compassionate individuals:\n\n" +
                "- 🤝 **220,000+ Givers** (volunteers who gave their time & resources)\n" +
                "- 🌱 **5,600,000+ Receivers** (lives positively impacted)\n" +
                "- 👀 **8,200,000+ Observers** (people inspired by witnessing kindness)\n" +
                "- 🌳 **Thousands of trees planted** with tribal communities\n" +
                "- 🍌 **Millions of meals** distributed to underprivileged children\n\n" +
                "And we are just getting started! Would you like to be a part of these growing numbers?"
            );

        //  9. CONTACT 
        } else if (
            cleanQuery.includes("contact") || cleanQuery.includes("email") ||
            cleanQuery.includes("phone") || cleanQuery.includes("address") ||
            cleanQuery.includes("location") || cleanQuery.includes("office") ||
            cleanQuery.includes("where are you") || cleanQuery.includes("reach you")
        ) {
            updateHistory(
                "We would absolutely love to hear from you! 💌\n\n" +
                "Here is how you can reach our wonderful team:\n\n" +
                "📧 **Email:** contactus@kindnessunlimited.in\n" +
                "📞 **Phone:** +91 9820710081\n" +
                "🏠 **Office:** A-102 Ajanta, Sant Muktabai Road, Vile Parle (East), Mumbai 400057\n" +
                "🕘 **Working Hours:** Monday - Saturday, 10:00 AM - 6:00 PM\n\n" +
                "We typically respond within 24 hours. Can't wait to hear from you!",
                { text: "Visit Page", url: "https://kindnessunlimited.in" }
            );

        //  10. THANK YOU 
        } else if (cleanQuery.includes("thank") || cleanQuery.includes("thx") || cleanQuery.includes("ty")) {
            updateHistory(
                "You are so very welcome! 😊\n\n" +
                "It truly warms our hearts to assist someone who cares about kindness. " +
                "If you ever have more questions or want to get involved, please don't hesitate to ask. Have a beautiful, kind day! 🌸"
            );

        //  11. OK / ACKNOWLEDGEMENT 
        } else if (
            cleanQuery === "ok" || cleanQuery === "okay" || cleanQuery === "cool" ||
            cleanQuery === "got it" || cleanQuery === "alright" || cleanQuery === "sure" ||
            cleanQuery === "nice" || cleanQuery === "great" || cleanQuery === "noted"
        ) {
            updateHistory(
                "Wonderful! 👍 Whenever you're ready, I'm right here.\n\n" +
                "Feel free to ask me anything about KU — projects, volunteering, events, donations, or anything else. I'm happy to help! 😊"
            );

        //  12. WORLD KINDNESS DAY / WKM ─
        } else if (
            cleanQuery.includes("world kindness day") || cleanQuery.includes("world kindness movement") ||
            cleanQuery.includes("november 13") || cleanQuery.includes("wkm") ||
            cleanQuery.includes("kindness day") || cleanQuery.includes("global kindness")
        ) {
            updateHistory(
                "Great question! 🌏\n\n" +
                "**World Kindness Day** is celebrated every year on **November 13th**. It's a global initiative to highlight the good deeds in the community and inspire others to do the same.\n\n" +
                "Kindness Unlimited is the **official Indian representative** of the **World Kindness Movement (WKM)** — a global coalition of kindness NGOs from over **28 countries** working together to make kindness the norm worldwide.\n\n" +
                "Every year, we celebrate World Kindness Day with events like The Kindness Jam. Would you like to participate this year?"
            );

        //  13. AGE / ELIGIBILITY / WHO CAN JOIN 
        } else if (
            cleanQuery.includes("who can") || cleanQuery.includes("age") ||
            cleanQuery.includes("eligible") || cleanQuery.includes("student") ||
            cleanQuery.includes("how old") || cleanQuery.includes("can i join") ||
            cleanQuery.includes("am i eligible") || cleanQuery.includes("criteria")
        ) {
            updateHistory(
                "Kindness has no age limit — everyone is welcome here! 💛\n\n" +
                "- 🏫 **School Students:** Join through **Project KUL**, our youth volunteering program in educational institutions.\n" +
                "- 🎓 **College Students:** Sign up on our website and participate in any activity that fits your schedule.\n" +
                "- 💼 **Working Professionals:** Volunteer on weekends or during drives — we have flexible options!\n" +
                "- 🏢 **Corporates & Teams:** Partner with us for group CSR volunteering events.\n" +
                "- 👴 **Senior Citizens:** Share your wisdom through teaching and mentoring sessions.\n\n" +
                "The only requirement? A genuine heart to make a difference! Ready to begin?",
                { text: "Register Now", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  14. AWARDS & RECOGNITION 
        } else if (
            cleanQuery.includes("award") || cleanQuery.includes("recogni") ||
            cleanQuery.includes("certif") || cleanQuery.includes("accreditation") ||
            cleanQuery.includes("honour") || cleanQuery.includes("honor") ||
            cleanQuery.includes("trust") || cleanQuery.includes("legal")
        ) {
            updateHistory(
                "We are truly humbled by the recognition our community has received! 🏆\n\n" +
                "Here are KU's key credentials:\n\n" +
                "- ✅ **Registered Trust** under Indian law\n" +
                "- ✅ **GuideStar Certified** — a global mark of transparency and accountability\n" +
                "- ✅ **80G Tax Exemption** — all donations are tax-deductible\n" +
                "- ✅ **Official Indian representative** of the World Kindness Movement\n\n" +
                "Our volunteers are also recognised with certificates, badges, and community appreciation for every contribution they make. 🎖️"
            );

        //  15. SOCIAL MEDIA 
        } else if (
            cleanQuery.includes("social media") || cleanQuery.includes("instagram") ||
            cleanQuery.includes("facebook") || cleanQuery.includes("twitter") ||
            cleanQuery.includes("linkedin") || cleanQuery.includes("youtube") ||
            cleanQuery.includes("follow") || cleanQuery.includes("social") ||
            cleanQuery.includes("online presence")
        ) {
            updateHistory(
                "We'd love to stay connected with you on social media! 📲\n\n" +
                "Follow Kindness Unlimited for inspiring stories, event updates, and volunteer highlights:\n\n" +
                "- **Instagram:** https://www.instagram.com/kindnessunlimited/\n" +
                "- **Facebook:** https://www.facebook.com/KindnessUnlimited.in/\n" +
                "- **LinkedIn:** https://www.linkedin.com/company/kindness-unlimited/\n" +
                "- **YouTube:** https://www.youtube.com/user/KindnessUnlimited\n\n" +
                "Hit follow and be part of a growing global movement of kind souls! 🌟"
            );

        //  16. KU TERMINOLOGY (GIVER / RECEIVER / OBSERVER) 
        } else if (
            cleanQuery.includes("giver") || cleanQuery.includes("receiver") ||
            cleanQuery.includes("observer") || cleanQuery.includes("terminology") ||
            cleanQuery.includes("meaning") || cleanQuery.includes("define") || cleanQuery.includes("what is a")
        ) {
            updateHistory(
                "Love that you asked — we do use some lovely special terms at KU! 📖\n\n" +
                "- 🤝 **Giver:** A volunteer who dedicates their time, skills, or resources to support our activities and causes.\n" +
                "- 🌱 **Receiver:** Someone who directly benefits from KU's programs — like a child receiving a meal through Project Nashta.\n" +
                "- 👀 **Observer:** Someone who witnesses an act of kindness and is inspired by it. This ripple effect is just as powerful!\n\n" +
                "Together, Givers, Receivers, and Observers form the beautiful ecosystem of change that KU nurtures every single day."
            );

        //  17. CORPORATE / CSR 
        } else if (
            cleanQuery.includes("corporate") || cleanQuery.includes("csr") ||
            cleanQuery.includes("company") || cleanQuery.includes("business") ||
            cleanQuery.includes("organisation") || cleanQuery.includes("organization") ||
            cleanQuery.includes("ngo partner") || cleanQuery.includes("collaborate") ||
            cleanQuery.includes("sponsor")
        ) {
            updateHistory(
                "We warmly welcome corporate partnerships! 🤝\n\n" +
                "KU offers meaningful **CSR collaboration** for companies that want to create genuine social impact:\n\n" +
                "- 🧑‍🤝‍🧑 **Group Volunteering Drives** — Engage your entire team in on-ground activities.\n" +
                "- 💰 **Project Sponsorships** — Fund specific initiatives like Project Nashta or Plant Kindness.\n" +
                "- 🎪 **Event Partnerships** — Co-host or sponsor The Kindness Jam and other flagship events.\n" +
                "- 📣 **Awareness Campaigns** — Run kindness-themed campaigns in partnership with KU.\n\n" +
                "We are GuideStar certified and 80G compliant — your CSR contributions are fully transparent and tax-deductible. Let's build something meaningful together!",
                { text: "Visit Our Page", url: "https://kindnessunlimited.in" }
            );

        //  18. SCHOOL / COLLEGE / CAMPUS 
        } else if (
            cleanQuery.includes("school") || cleanQuery.includes("college") ||
            cleanQuery.includes("university") || cleanQuery.includes("campus") ||
            cleanQuery.includes("institution") || cleanQuery.includes("kul chapter") ||
            cleanQuery.includes("youth club")
        ) {
            updateHistory(
                "We are very active in schools and colleges across Mumbai! 🎓\n\n" +
                "**Project KUL (Kindness Unlimited League)** is our dedicated youth volunteering structure inside educational institutions:\n\n" +
                "- 📌 KUL Chapters are set up within your school or college.\n" +
                "- 🎯 Students participate in volunteering events, awareness drives, and community outreach.\n" +
                "- 🏅 Students earn certificates, KU points, and recognition for their contributions.\n" +
                "- 🌍 It's a great way to build your social profile and make a real difference!\n\n" +
                "Want to start a KUL chapter at your institution? We'd love to hear from you!",
                { text: "Get in Touch", url: "https://kindnessunlimited.in" }
            );

        //  19. ACTIVITIES / WHAT VOLUNTEERS DO 
        } else if (
            cleanQuery.includes("activit") || cleanQuery.includes("what do volunteers do") ||
            cleanQuery.includes("what can i do") || cleanQuery.includes("how to help") ||
            cleanQuery.includes("task") || cleanQuery.includes("what kind of work") ||
            cleanQuery.includes("type of volunteering")
        ) {
            updateHistory(
                "There are so many wonderful ways to contribute — we have something for everyone! 🙌\n\n" +
                "Here's a snapshot of KU volunteering activities:\n\n" +
                "- 🍌 **Meal Distribution** — Help distribute nutritious food to underprivileged children.\n" +
                "- 🌳 **Tree Planting** — Plant fruit trees with tribal communities for a greener tomorrow.\n" +
                "- 🏖️ **Beach Clean-ups** — Join drives to restore Mumbai's beautiful coastlines.\n" +
                "- 📚 **Teaching & Tutoring** — Share your skills and knowledge with children who need it most.\n" +
                "- 🎪 **Event Support** — Help organize flagship events like The Kindness Jam.\n" +
                "- 🎨 **Creative Workshops** — Run art, music, or craft sessions for underprivileged youth.\n\n" +
                "Browse all open activities on our website and sign up for what excites you!",
                { text: "View Activities", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  20. REWARDS / POINTS SYSTEM 
        } else if (
            cleanQuery.includes("reward") || cleanQuery.includes("point") ||
            cleanQuery.includes("benefit") || cleanQuery.includes("perks") ||
            cleanQuery.includes("earn") || cleanQuery.includes("incentive") ||
            cleanQuery.includes("what do i get")
        ) {
            updateHistory(
                "Yes, kindness pays — in the best way possible! 🎁\n\n" +
                "At KU, every act of giving is celebrated. Here's how our rewards system works:\n\n" +
                "- 🏅 Every volunteering activity you complete earns you **KU Points**.\n" +
                "- Points accumulate and can be redeemed for special **rewards and recognition**.\n" +
                "- Top volunteers receive **certificates, badges, and community shout-outs**.\n" +
                "- Earn enough points and you could win **free entry to The Kindness Jam**! 🎉\n" +
                "- Outstanding Givers are featured on our **Hall of Kindness** on the website.\n\n" +
                "It's our heartfelt way of saying thank you for your generosity. 😊",
                { text: "Sign Up & Start Earning", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  21. HISTORY / FOUNDING 
        } else if (
            cleanQuery.includes("history") || cleanQuery.includes("founded") ||
            cleanQuery.includes("founder") || cleanQuery.includes("started") ||
            cleanQuery.includes("when was") || cleanQuery.includes("how did ku") ||
            cleanQuery.includes("origin") || cleanQuery.includes("background")
        ) {
            updateHistory(
                "Every great movement has an inspiring origin story! 📜\n\n" +
                "**Kindness Unlimited** was founded in **2005 in Mumbai** with a simple but powerful belief — " +
                "that kindness, when practised consistently, can drive real social and economic change.\n\n" +
                "What started as a small volunteer-driven group has grown over two decades into a nationally recognised organisation:\n" +
                "- 🌍 Represents India in the **World Kindness Movement** (28+ countries)\n" +
                "- 🤝 Has engaged over **220,000 Givers** and impacted **5.6 million+ lives**\n" +
                "- 🚀 Runs multiple flagship projects across nutrition, environment, and youth development\n\n" +
                "All powered by the belief that one act of kindness can truly change the world. 🌍"
            );

        //  22. TAX EXEMPTION / 80G 
        } else if (
            cleanQuery.includes("tax") || cleanQuery.includes("80g") ||
            cleanQuery.includes("deduction") || cleanQuery.includes("exemption") ||
            cleanQuery.includes("receipt") || cleanQuery.includes("certificate for donation")
        ) {
            updateHistory(
                "Great news for donors! 🎉\n\n" +
                "Kindness Unlimited is registered under **Section 80G** of the Income Tax Act, which means your donations are **eligible for tax deductions** in India.\n\n" +
                "Here's what you receive when you donate:\n" +
                "- 🧾 An official **donation receipt** for your records.\n" +
                "- 📄 An **80G certificate** that you can use when filing your taxes.\n" +
                "- ❤️ The satisfaction of knowing your money is creating real change!\n\n" +
                "Every rupee donated goes directly to our on-ground projects.",
                { text: "Donate Now", url: "https://kindnessunlimited.in/donate" }
            );

        //  23. HOW IS MY DONATION USED 
        } else if (
            cleanQuery.includes("how is my donation used") || cleanQuery.includes("where does the money go") ||
            cleanQuery.includes("use of funds") || cleanQuery.includes("transparency") ||
            cleanQuery.includes("where does donation go") || cleanQuery.includes("how money is spent")
        ) {
            updateHistory(
                "That is a wonderful and responsible question! We deeply value transparency. 🌟\n\n" +
                "Every donation made to Kindness Unlimited is channelled directly into our projects:\n\n" +
                "- 🍌 **Project Nashta** — Nutritious meals for underprivileged children.\n" +
                "- 🌳 **Plant Kindness** — Fruit trees planted with tribal communities.\n" +
                "- 📚 **Education Support** — Teaching materials and tutoring for children in need.\n" +
                "- 🎪 **Events & Awareness** — Organizing community-building events like The Kindness Jam.\n\n" +
                "We are **GuideStar Certified**, which independently verifies our financial transparency and governance. You can trust that your kindness is in good hands.",
                { text: "Donate Securely", url: "https://kindnessunlimited.in/donate" }
            );

        //  24. MUMBAI / WHERE KU OPERATES 
        } else if (
            cleanQuery.includes("mumbai") || cleanQuery.includes("where do you operate") ||
            cleanQuery.includes("city") || cleanQuery.includes("which city") ||
            cleanQuery.includes("pan india") || cleanQuery.includes("outside mumbai") ||
            cleanQuery.includes("nationwide") || cleanQuery.includes("operate")
        ) {
            updateHistory(
                "We are primarily based in **Mumbai, Maharashtra** — the city of dreams! 🏙️\n\n" +
                "Most of our on-ground activities, such as beach clean-ups, Project Nashta distributions, and The Kindness Jam, take place across Mumbai.\n\n" +
                "However, our impact extends beyond the city:\n" +
                "- 🌳 **Plant Kindness** works with the Kathkari tribe in rural Maharashtra.\n" +
                "- 🎓 **Project KUL** is expanding into institutions across the country.\n" +
                "- 🌍 As the Indian representative of the **World Kindness Movement**, we are part of a global network spanning **28+ countries**.\n\n" +
                "Whether you're in Mumbai or beyond, there is always a way to be part of our journey!"
            );

        //  25. KATHKARI TRIBE / PLANT KINDNESS DETAILS 
        } else if (
            cleanQuery.includes("kathkari") || cleanQuery.includes("tribe") ||
            cleanQuery.includes("tribal") || cleanQuery.includes("fruit tree") ||
            cleanQuery.includes("livelihood") || cleanQuery.includes("environment") ||
            cleanQuery.includes("nature") || cleanQuery.includes("green") ||
            cleanQuery.includes("tree")
        ) {
            updateHistory(
                "We love talking about **Plant Kindness** — it's truly close to our hearts! 🌳\n\n" +
                "The **Kathkari tribe** is an indigenous tribal community in Maharashtra facing serious challenges to their livelihood and way of life.\n\n" +
                "Through **Plant Kindness**, KU:\n" +
                "- 🌱 Plants **fruit-bearing trees** on tribal lands to provide a sustainable food and income source.\n" +
                "- 🤝 Works directly alongside tribal families, ensuring community ownership and pride.\n" +
                "- 🌍 Combines environmental conservation with direct economic empowerment.\n\n" +
                "It's a beautiful example of how kindness can heal people and the planet at the same time."
            );

        //  26. PROJECT NASHTA DETAILS 
        } else if (
            cleanQuery.includes("nashta") || cleanQuery.includes("food") ||
            cleanQuery.includes("meal") || cleanQuery.includes("nutrition") ||
            cleanQuery.includes("hunger") ||
            cleanQuery.includes("children food") || cleanQuery.includes("underprivilege")
        ) {
            updateHistory(
                "Project Nashta is one of our most beloved initiatives! \n\n" +
                "**The Problem:** Millions of children in India start their school day on an empty stomach, making it nearly impossible to focus and learn.\n\n" +
                "**Our Solution:** We provide **culturally contextual, nutritionally appropriate meals**.\n\n" +
                "Volunteers help distribute these meals during our regular drives.",
                { text: "Volunteer Now", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  27. IS KINDNESS FREE / FEES 
        } else if (
            cleanQuery.includes("free") || cleanQuery.includes("fee") ||
            cleanQuery.includes("cost") || cleanQuery.includes("paid") ||
            cleanQuery.includes("charges") || cleanQuery.includes("membership fee") ||
            cleanQuery.includes("do i have to pay")
        ) {
            updateHistory(
                "Absolutely! Volunteering with Kindness Unlimited is completely **free of charge**. 🎉\n\n" +
                "There are no membership fees, registration charges, or hidden costs to join our community. All you invest is your **time, energy, and kindness** — and that's worth more than anything to us!\n\n" +
                "You can sign up on our website, browse activities, and start volunteering right away at no cost. 😊",
                { text: "Register for Free", url: "https://kindnessunlimited.in/volunteering" }
            );

        //  28. HELLO / CHATBOT INTRO 
        } else if (
            cleanQuery.includes("who are you") || cleanQuery.includes("are you a bot") ||
            cleanQuery.includes("are you human") || cleanQuery.includes("what can you do") ||
            cleanQuery.includes("what can you help") || cleanQuery.includes("what can you tell") ||
            cleanQuery.includes("help me")
        ) {
            updateHistory(
                "Hello! I'm the **KU Assistant** 🤖💛 — your friendly guide to everything about Kindness Unlimited!\n\n" +
                "Here's what I can help you with:\n\n" +
                "- 🌟 Learn about **who KU is** and our story\n" +
                "- 🙌 Find out **how to volunteer** and get involved\n" +
                "- ❤️ Understand **how to donate** and support our projects\n" +
                "- 🎉 Discover **events** like The Kindness Jam\n" +
                "- 📊 Explore our **impact** and ongoing initiatives\n" +
                "- 📞 Get our **contact details**\n\n" +
                "Just type your question and I'll do my best to help. What would you like to know? 😊"
            );

        //  DEFAULT FALLBACK (POLITE) 
        } else {
            updateHistory(
                "Thank you so much for reaching out — it truly means a lot to us! 🙏\n\n" +
                "I'm still learning, and I may not have the perfect answer for that just yet. " +
                "But I don't want to leave you without help! Here's what you can do:\n\n" +
                "📧 **Email us:** contactus@kindnessunlimited.in\n" +
                "📞 **Call us:** +91 9820710081\n\n" +
                "Our lovely team will be more than happy to assist you personally. 💛\n\n" +
                "Or feel free to ask me something else — I'm here for anything about our **projects, volunteering, events, donations, impact, or how to join**! 😊",
            );
        }
    }
};

    // Handler for when a menu button is clicked
    const handleMenuClick = (option) => {
        if (option.type === "link") {
            // Instantly open the redirecting link in a new tab
            window.open(option.url, "_blank", "noopener,noreferrer");
        } else {
            const userMessage = option.text;

            // 1. Create the new history array locally
            const updatedHistory = [...chatHistory, { role: "user", text: userMessage }];

            // 2. Update the state with the user's message
            setChatHistory(updatedHistory);

            // 3. Trigger the bot response OUTSIDE the state updater function
            setTimeout(() => {
                setChatHistory((currentHistory) => [...currentHistory, { role: "model", text: "Thinking..." }]);
                generateBotResponse([...updatedHistory, { role: "user", text: `Using the details provided above, please address this query: ${userMessage}` }]);
            }, 600);
        }
    };

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [chatHistory]);

    return (
        <div className={`container ${showChatbot ? "show-chatbot" : ""}`}>
            
            {/* UPDATED: Added showWelcomePopup condition and the Close button */}
            {!showChatbot && showWelcomePopup && (
                <div className="chatbot-welcome-popup">
                    <span>Hi, I'm KU Chatbot!!</span>
                    <button 
                        className="welcome-close-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowWelcomePopup(false);
                        }}
                        title="Dismiss"
                        type="button"
                    >
                        <span className="material-symbols-rounded">close</span>
                    </button>
                </div>
            )}

            <button
                onClick={() => setShowChatbot((prev) => !prev)}
                id="chatbot-toggler"
                title="Chat with KU Bot"
                type="button"
            >
                <span className="material-symbols-rounded icon-open">smart_toy</span>
                <span className="material-symbols-rounded icon-close">close</span>
            </button>

            <div className="chatbot-popup">
                {/* chatbot header */}
                <div className="chat-header">
                    <div className="header-info">
                        <Chatboticon />
                        <h2 className="logo-text">KU Chatbot</h2>
                    </div>
                    <button
                        onClick={() => setShowChatbot((prev) => !prev)}
                        className="material-symbols-rounded"
                        type="button"
                        title="Close chat"
                    >
                        keyboard_arrow_down
                    </button>
                </div>

                {/* chatbot body */}
                <div ref={chatBodyRef} className="chat-body">
                    <div className="message bot-message">
                        <Chatboticon />
                        <p className="message-text">
                            Hi there, <br /> Welcome to KU!!! <br /> How can I help you today?
                        </p>
                    </div>

                    {/* Quick Menu Container */}
                    <div className="chatbot-menu-container">
                        <div className="menu-header">
                            <p className="menu-title">Menu</p>
                        </div>
                        <div className="menu-options-list">
                            {menuOptions.map((option, index) => (
                                <button
                                    key={index}
                                    className="menu-option-btn"
                                    onClick={() => handleMenuClick(option)}
                                    type="button"
                                >
                                    <span className="material-symbols-rounded btn-icon">
                                        {option.type === "link" ? "volunteer_activism" : "help_center"}
                                    </span>
                                    {option.text}
                                </button>
                            ))}
                        </div>
                    </div>

                    {chatHistory.map((chat, index) => (
                        <ChatMessage key={index} chat={chat} />
                    ))}
                </div>

                <div className="chat-footer">
                    <ChatForm
                        chatHistory={chatHistory}
                        setChatHistory={setChatHistory}
                        generateBotResponse={generateBotResponse}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatBot;